import { API_BASE_URL } from "../../../config";
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

const EditSalePriceList = () => {
    const { id: sellerId } = useParams(); // sellerId (U-... uid)
    const navigate = useNavigate();
    const { state } = useLocation();
    const [sellerName, setSellerName] = useState(state?.sellerName || '');
    const [rows, setRows] = useState([]);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const getToken = () => {
        const user = localStorage.getItem('user');
        return user ? JSON.parse(user).token : null;
    };

    const loadData = useCallback(async () => {
        const token = getToken();
        if (!token) {
            navigate('/login');
            return;
        }

        setLoading(true);
        setError('');
        try {
            const pricesResponse = await fetch(`${API_BASE_URL}/seller/api/default-prices/seller/${sellerId}`, {
                headers: {
                    Accept: 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            });

            if (!pricesResponse.ok) {
                if (pricesResponse.status === 403) {
                    setError('ไม่มีสิทธิ์เข้าถึงข้อมูลนี้ (403)');
                    return;
                }
                const errorText = await pricesResponse.text();
                setError(`Failed to load sale price list: ${errorText}`);
                return;
            }

            const priceRows = await pricesResponse.json();

            const itemsResponse = await fetch(`${API_BASE_URL}/seller/api/product-items`, {
                headers: {
                    Accept: 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            });

            let iceNameByCode = {};
            if (itemsResponse.ok) {
                const itemTypesData = await itemsResponse.json();
                const rawItems = Array.isArray(itemTypesData) ? itemTypesData : (Array.isArray(itemTypesData?.data) ? itemTypesData.data : []);
                iceNameByCode = rawItems.reduce((acc, it) => {
                    const code = it.productCode || it.code || it.product_code;
                    if (code) acc[code] = it.productName || it.name || code;
                    return acc;
                }, {});
            }

            setRows((priceRows || []).map(row => ({
                id: row.id,
                iceCode: row.iceCode,
                iceName: iceNameByCode[row.iceCode] || row.iceCode,
                sellerId: row.sellerId,
                price: row.price,
                originalPrice: row.price,
            })));

            if (!state?.sellerName) {
                const withUserResponse = await fetch(`${API_BASE_URL}/seller/api/default-prices/with-user`, {
                    headers: {
                        Accept: 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                });
                if (withUserResponse.ok) {
                    const withUserData = await withUserResponse.json();
                    const match = (withUserData || []).find(item => item.sellerId === sellerId);
                    if (match?.sellerName) setSellerName(match.sellerName);
                }
            }
        } catch (err) {
            setError('Error fetching sale price list. Please check your connection.');
        } finally {
            setLoading(false);
        }
    }, [sellerId, navigate, state]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const handlePriceChange = (rowId, newPrice) => {
        setRows(prev => prev.map(row =>
            row.id === rowId ? { ...row, price: newPrice } : row
        ));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const token = getToken();
        if (!token) {
            navigate('/login');
            return;
        }

        const dirtyRows = rows.filter(row => row.price !== row.originalPrice);
        if (dirtyRows.length === 0) {
            navigate('/salepriceAndItemManage');
            return;
        }

        setSaving(true);
        setError('');

        const savedIds = [];
        for (const row of dirtyRows) {
            try {
                const response = await fetch(`${API_BASE_URL}/seller/api/default-prices/${row.id}`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        Accept: 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        id: row.id,
                        iceCode: row.iceCode,
                        sellerId: row.sellerId,
                        price: row.price,
                    }),
                });

                if (!response.ok) {
                    const savedNote = savedIds.length > 0
                        ? ` รายการที่บันทึกไปแล้ว (${savedIds.join(', ')}) ยังคงถูกบันทึกอยู่`
                        : '';
                    setError(`บันทึกราคา ${row.iceName} ไม่สำเร็จ (สถานะ ${response.status}).${savedNote}`);
                    setRows(prev => prev.map(r =>
                        savedIds.includes(r.id) ? { ...r, originalPrice: r.price } : r
                    ));
                    setSaving(false);
                    return;
                }

                savedIds.push(row.id);
            } catch (err) {
                const savedNote = savedIds.length > 0
                    ? ` รายการที่บันทึกไปแล้ว (${savedIds.join(', ')}) ยังคงถูกบันทึกอยู่`
                    : '';
                setError(`บันทึกราคา ${row.iceName} ไม่สำเร็จ.${savedNote}`);
                setRows(prev => prev.map(r =>
                    savedIds.includes(r.id) ? { ...r, originalPrice: r.price } : r
                ));
                setSaving(false);
                return;
            }
        }

        setSaving(false);
        navigate('/salepriceAndItemManage');
    };

    if (loading) {
        return <p>Loading...</p>;
    }

    return (
        <div className="edit-sale-price-list">
            <h1>แก้ไขราคาขาย{sellerName ? ` — ${sellerName}` : ''}</h1>
            <button onClick={() => navigate('/salepriceAndItemManage')} className="backButton">
                กลับ
            </button>
            {error && <p className="error-message">{error}</p>}
            <form onSubmit={handleSubmit} className="sale-price-form">
                <div className="user-group">
                    {rows.map((row) => (
                        <div key={row.id} className="edit-item">
                            <label>
                                <span style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                                    {row.iceName}
                                </span>
                            </label>
                            <p />
                            <label>
                                ราคา:
                                <input
                                    type="number"
                                    value={row.price}
                                    onChange={(e) =>
                                        handlePriceChange(row.id, parseFloat(e.target.value))
                                    }
                                />
                            </label>
                            <hr />
                        </div>
                    ))}
                </div>
                <button type="submit" className="save-button" disabled={saving}>
                    {saving ? 'กำลังบันทึก...' : 'บันทึก'}
                </button>
            </form>
        </div>
    );
};

export default EditSalePriceList;
